import type { V2RateHistoryPoint, V2Token } from '../types/v2';

export interface ChartCoordinate {
  x: number;
  y: number;
}

export interface ChartGeometry {
  linePath: string;
  areaPath: string;
  coordinates: ChartCoordinate[];
  min: number;
  max: number;
}

export interface MarketTokenSummary {
  token: V2Token;
  symbol: string;
  name: string;
  iconUrl: string | null;
  isPopular: boolean;
}

export const parseHistoryPrices = (points: V2RateHistoryPoint[]): number[] => (
  points
    .map(point => Number(point.price_usd))
    .filter(price => Number.isFinite(price) && price > 0)
);

export const calculatePeriodChange = (points: V2RateHistoryPoint[]) => {
  const prices = parseHistoryPrices(points);
  if (prices.length < 2) {
    return { absolute: 0, percent: 0, isPositive: true };
  }
  const first = prices[0];
  const last = prices[prices.length - 1];
  const absolute = last - first;
  return {
    absolute,
    percent: first === 0 ? 0 : (absolute / first) * 100,
    isPositive: absolute >= 0,
  };
};

export const buildChartGeometry = (
  input: number[],
  width: number,
  height: number,
  verticalPadding = 4,
): ChartGeometry | null => {
  const prices = input.filter(price => Number.isFinite(price) && price > 0);
  if (prices.length === 0 || width <= 0 || height <= 0) { return null; }

  const rawMin = Math.min(...prices);
  const rawMax = Math.max(...prices);
  const visibleRange = rawMax - rawMin || Math.max(Math.abs(rawMax) * 0.02, 1);
  const min = rawMin === rawMax ? rawMin - visibleRange / 2 : rawMin;
  const max = rawMin === rawMax ? rawMax + visibleRange / 2 : rawMax;
  const drawableHeight = Math.max(1, height - verticalPadding * 2);
  const denominator = Math.max(1, prices.length - 1);
  const coordinates = prices.map((price, index) => ({
    x: (index / denominator) * width,
    y: verticalPadding + ((max - price) / (max - min)) * drawableHeight,
  }));

  // A single quote is rendered as a horizontal segment, not an invisible dot.
  if (coordinates.length === 1) {
    coordinates.push({ x: width, y: coordinates[0].y });
  }

  let linePath = `M ${coordinates[0].x.toFixed(2)} ${coordinates[0].y.toFixed(2)}`;
  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1];
    const current = coordinates[index];
    const controlX = (previous.x + current.x) / 2;
    linePath += ` C ${controlX.toFixed(2)} ${previous.y.toFixed(2)}, ${controlX.toFixed(2)} ${current.y.toFixed(2)}, ${current.x.toFixed(2)} ${current.y.toFixed(2)}`;
  }
  const areaPath = `${linePath} L ${width.toFixed(2)} ${height.toFixed(2)} L 0 ${height.toFixed(2)} Z`;
  return { linePath, areaPath, coordinates, min, max };
};

/** xStock tokens live in the catalogue but belong to the Stocks section,
 *  not to crypto market lists. */
export const isXStockToken = (token: Pick<V2Token, 'token_type'>): boolean =>
  token.token_type === 'xstock';

/**
 * Fiat in the catalogue is a rate, not a market: EUR is there so a card
 * balance can be priced and a top-up converted, and it sat in the token
 * lists with a price, no chart and nothing to do with it.
 *
 * By type rather than by symbol — a hidden "EUR" would come straight back
 * the day someone adds USD.
 */
export const isFiatToken = (token: Pick<V2Token, 'token_type'>): boolean =>
  token.token_type?.toLowerCase() === 'fiat';

export const buildUniqueMarketTokens = (tokens: V2Token[]): MarketTokenSummary[] => {
  const bySymbol = new Map<string, MarketTokenSummary>();
  for (const token of tokens) {
    if (!token.is_active || isXStockToken(token) || isFiatToken(token)) { continue; }
    const symbol = token.symbol.trim().toUpperCase();
    if (!symbol) { continue; }
    const existing = bySymbol.get(symbol);
    if (!existing) {
      bySymbol.set(symbol, {
        token,
        symbol,
        name: token.name || symbol,
        iconUrl: token.icon_url ?? null,
        isPopular: token.is_popular_token === true,
      });
      continue;
    }
    existing.isPopular = existing.isPopular || token.is_popular_token === true;
    if (!existing.iconUrl && token.icon_url) {
      existing.iconUrl = token.icon_url;
      existing.token = token;
    }
  }
  return Array.from(bySymbol.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
};
